import { toValidDate } from "./form";

export interface DraftVisit {
	visitNumber: number;
	/** Null while nobody has scheduled this Visit yet. */
	date: Date | null;
	/** Null while nobody is assigned. */
	therapistName: string | null;
}

/**
 * Upper bound on the rendered list. formData is a schemaless JSONB column written by
 * two producers — this form and fisiohome-backend, for a Draft the customer created —
 * so a single malformed numberOfVisit is enough to ask for an arbitrarily long list.
 * Failing that way costs the whole Drafts page, not one label, so the list is clamped
 * well above any real package instead.
 */
const MAX_VISITS = 60;

export function draftVisits(formData: Record<string, any> | null | undefined) {
	const scheduling = formData?.appointmentScheduling;
	if (!scheduling) return [];

	const byNumber = new Map<number, DraftVisit>();
	const read = (source: any): Omit<DraftVisit, "visitNumber"> => {
		// Not `name?.trim()`: on a non-string that is a TypeError rather than undefined,
		// and formData carries whatever its two producers wrote.
		const name = source?.therapist?.name;
		return {
			date: toValidDate(source?.appointmentDateTime),
			therapistName: (typeof name === "string" && name.trim()) || null,
		};
	};

	// Visit 1 is the TOP-LEVEL scheduling — seriesVisits holds Visits 2..n only, the
	// same shape therapistsOf() in form.ts walks — so reading seriesVisits alone loses
	// the one Visit every Booking has.
	byNumber.set(1, { visitNumber: 1, ...read(scheduling) });

	const series = (
		Array.isArray(scheduling.seriesVisits) ? scheduling.seriesVisits : []
	).slice(0, MAX_VISITS);

	// Two passes, so an entry with a broken visitNumber cannot take the slot a
	// well-formed sibling is entitled to. Fall back to position for an entry whose
	// number was never written — the array is Visits 2..n, so index 0 is Visit 2.
	const leftovers: any[] = [];
	series.forEach((visit: any, index: number) => {
		const preferred = Number(visit?.visitNumber ?? index + 2);
		if (
			Number.isInteger(preferred) &&
			preferred >= 1 &&
			!byNumber.has(preferred)
		)
			byNumber.set(preferred, { visitNumber: preferred, ...read(visit) });
		else leftovers.push(visit);
	});

	// Malformed or duplicated numbers get appended rather than dropped: the Draft still
	// owes that Visit, and one missing from the list reads as one never booked.
	for (const visit of leftovers) {
		const visitNumber = Math.max(...byNumber.keys()) + 1;
		byNumber.set(visitNumber, { visitNumber, ...read(visit) });
	}

	// Padded to the package size: a Draft abandoned before the scheduling step has no
	// seriesVisits at all, and the empty rows are what show how much is still unplanned.
	const total = Math.min(
		MAX_VISITS,
		Math.max(
			...byNumber.keys(),
			Number(scheduling.package?.numberOfVisit) || 0,
		),
	);

	return Array.from(
		{ length: total },
		(_, index): DraftVisit =>
			byNumber.get(index + 1) ?? {
				visitNumber: index + 1,
				date: null,
				therapistName: null,
			},
	);
}
